/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { FC } from "react";
import { Button } from "react-bootstrap";
import { DownloadAsMenu } from "../DownloadAsMenu";
import { DownloadAsMenuData } from "@src/utils/downloadAs";
import { ImagesViewerModalProps } from "./ViewerModal";
import { ViewerButtonType } from "@advapacs/common/src/constant";
import { AHSTooltip } from "@advapacs/common/src/components/Tooltips/AHSTooltip";
import DownloadWhiteIcon from "@src/assets/icons/download-white.svg";
import ChevronRight from "@src/assets/icons/chevron-right.svg";
import ChevronLeft from "@src/assets/icons/chevron-left.svg";
import PlusIcon from "@src/assets/icons/plus-icon.svg";
import MinusIcon from "@src/assets/icons/minus-icon.svg";
import OneIcon from "@src/assets/icons/1-1-icon.svg";
import RedoIcon from "@src/assets/icons/redo-alt.svg";
import ArrowAltHIcon from "@src/assets/icons/arrows-alt-h.svg";
import ArrowAltVIcon from "@src/assets/icons/arrows-alt-v.svg";
import ReplyFillLeft from "@src/assets/icons/reply-fill-left.svg";
import ReplyFillRight from "@src/assets/icons/reply-fill-right.svg";
import ShareIcon from "@src/assets/icons/share_1.svg";

interface ViewerFooterProps {
  mobileLayout?: boolean;
  isTabletOnly?: boolean;
  loading?: boolean;
  imgLoading?: boolean;
  studyId?: string;
  indexSeriesImage?: number;
  seriesImagesViewerModal?: Array<ImagesViewerModalProps>;
  handleBack: () => void;
  handleNext: () => void;
  showPrevInstance: () => void;
  showNextInstance: () => void;
  indexInstanceImage?: number;
  instanceImagesViewerModal?: Array<ImagesViewerModalProps>;
  handleIconClick: (type: ViewerButtonType) => void;
  downloadInstance?: () => void;
  downloadAsMenu?: DownloadAsMenuData;
  handleShareImageInstance?: () => void;
}

export const ViewerFooter: FC<ViewerFooterProps> = ({
  mobileLayout,
  isTabletOnly,
  loading,
  indexInstanceImage,
  indexSeriesImage,
  seriesImagesViewerModal,
  instanceImagesViewerModal,
  downloadInstance,
  downloadAsMenu,
  handleBack,
  handleNext,
  showPrevInstance,
  showNextInstance,
  handleIconClick,
  handleShareImageInstance,
  imgLoading,
}) => {
  const showDeviceShareImageButton = navigator.canShare;

  const seriesImages = seriesImagesViewerModal ?? [];
  const instanceImages = instanceImagesViewerModal ?? [];
  // @ts-ignore
  const isLastSeries = indexSeriesImage + 1 === seriesImages.length;
  // @ts-ignore
  const isLastInstance = indexInstanceImage + 1 === instanceImages.length;

  return (
    <>
      {!mobileLayout && (
        <>
          <div className="flex-container">
            <div className="series-navigator pp-series-navigator">
              <AHSTooltip
                placement="top"
                id="viewer-toolbar-previous-series"
                description="Previous Series"
              >
                <Button
                  disabled={loading || indexSeriesImage === 0}
                  variant="light"
                  onClick={handleBack}
                  className="nav-button m-r-xxs"
                >
                  <img
                    className="padding-b-3px"
                    src={ChevronLeft}
                    alt="Prev Icon"
                  />
                </Button>
              </AHSTooltip>
              <p className="m-b-none m-r-xs">Series</p>
              {seriesImages.length > 0 ? indexSeriesImage + 1 : 0}/
              {seriesImages.length}
              <AHSTooltip
                placement="top"
                id="viewer-toolbar-next-series"
                description="Next Series"
              >
                <Button
                  disabled={loading || isLastSeries}
                  variant="light"
                  onClick={handleNext}
                  className="nav-button m-l-xxs "
                >
                  <img
                    className="padding-b-3px"
                    src={ChevronRight}
                    alt="Next Icon"
                  />
                </Button>
              </AHSTooltip>
            </div>
            <div className="d-flex align-items-center vertical-line"></div>
            <div className="series-navigator pp-series-navigator">
              <AHSTooltip
                placement="top"
                id="viewer-toolbar-previous-image"
                description="Previous Image"
              >
                <Button
                  disabled={loading || indexInstanceImage === 0}
                  variant="light"
                  onClick={showPrevInstance}
                  className="nav-button m-r-xxs "
                >
                  <img
                    className="padding-b-3px"
                    src={ChevronLeft}
                    alt="Prev Icon"
                  />
                </Button>
              </AHSTooltip>
              <p className="m-b-none m-r-xs">Images</p>
              {instanceImages.length > 0 ? indexInstanceImage + 1 : 0}/
              {instanceImages.length}
              <AHSTooltip
                placement="top"
                id="viewer-toolbar-next-image"
                description="Next Image"
              >
                <Button
                  disabled={
                    loading || instanceImages.length === 0 || isLastInstance
                  }
                  variant="light"
                  onClick={showNextInstance}
                  className="nav-button m-l-xxs"
                >
                  <img
                    className="padding-b-3px"
                    src={ChevronRight}
                    alt="Next Icon"
                  />
                </Button>
              </AHSTooltip>
            </div>
          </div>

          <div className="series-navigator pp-series-navigator">
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-zoom-in"
              description="Zoom In"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.ZOOM_IN)}
                className="nav-button m-l-xxs"
              >
                <img className="padding-b-3px" src={PlusIcon} alt="Next Icon" />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-zoom-out"
              description="Zoom Out"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.ZOOM_OUT)}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={MinusIcon}
                  alt="Next Icon"
                />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-actual-size"
              description="Actual Size (1:1)"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.ONT_TO_ONE)}
                className="nav-button m-l-xxs"
              >
                <img className="padding-b-3px" src={OneIcon} alt="Next Icon" />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-reset-view"
              description="Reset View"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.RESET)}
                className="nav-button m-l-xxs"
              >
                <img className="padding-b-3px" src={RedoIcon} alt="Next Icon" />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-rotate-left"
              description="Rotate Left"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.ROTATE_LEFT)}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ReplyFillLeft}
                  alt="Next Icon"
                />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-rotate-right"
              description="Rotate Right"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.ROTATE_RIGHT)}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ReplyFillRight}
                  alt="Next Icon"
                />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-flip-horizontal"
              description="Flip Horizontal"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() =>
                  handleIconClick(ViewerButtonType.FLIP_HORIZONTAL)
                }
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ArrowAltHIcon}
                  alt="Next Icon"
                />
              </Button>
            </AHSTooltip>
            <AHSTooltip
              placement="top"
              id="viewer-toolbar-flip-vertical"
              description="Flip Vertical"
            >
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.FLIP_VERTICAL)}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ArrowAltVIcon}
                  alt="Next Icon"
                />
              </Button>
            </AHSTooltip>
          </div>
          <div className="series-navigator justify-content-end">
            {/* @ts-ignore */}
            {showDeviceShareImageButton ? (
              <button
                className="btn pp-btn-light m-l-xxs mr-1"
                onClick={handleShareImageInstance}
                disabled={imgLoading}
              >
                <img
                  className="padding-b-3px m-r-xxs"
                  src={ShareIcon}
                  alt="Share Image Icon"
                />
                <span>Share Image</span>
              </button>
            ) : (
              <></>
            )}
            {downloadAsMenu ? (
              <DownloadAsMenu
                variant="viewer"
                showLabel
                toggleClassName="btn btn-primary"
                disabled={instanceImages.length === 0}
                {...downloadAsMenu}
              />
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => {
                  downloadInstance?.();
                }}
                disabled={instanceImages.length === 0}
              >
                <img
                  className="m-r-xxs padding-b-3px"
                  src={DownloadWhiteIcon}
                  alt="Download White Icon"
                />
                <span>Download</span>
              </button>
            )}
          </div>
        </>
      )}
      {mobileLayout && (
        <>
          <div className="flex-container w-100">
            <div className="series-navigator pp-series-navigator flex-1">
              <Button
                disabled={loading || indexSeriesImage === 0}
                variant="light"
                onClick={handleBack}
                className="nav-button m-r-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ChevronLeft}
                  alt="Prev Icon"
                />
              </Button>
              <div
                className={`text-center p-lr-xs ${
                  isTabletOnly ? "d-flex" : ""
                }`}
              >
                <p className={`m-b-none  ${isTabletOnly ? "m-r-xs" : ""}`}>
                  Series
                </p>
                <p className="m-b-none">
                  {seriesImages.length > 0 ? indexSeriesImage + 1 : 0}/
                  {seriesImages.length}
                </p>
              </div>

              <Button
                disabled={loading || isLastSeries}
                variant="light"
                onClick={handleNext}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ChevronRight}
                  alt="Next Icon"
                />
              </Button>
            </div>
            <div className="d-flex align-items-center vertical-line"></div>
            <div className="series-navigator pp-series-navigator flex-1">
              <Button
                disabled={loading || indexInstanceImage === 0}
                variant="light"
                onClick={showPrevInstance}
                className="nav-button m-r-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ChevronLeft}
                  alt="Prev Icon"
                />
              </Button>
              <div
                className={`text-center p-lr-xs ${
                  isTabletOnly ? "d-flex" : ""
                }`}
              >
                <p className={`m-b-none  ${isTabletOnly ? "m-r-xs" : ""}`}>
                  Images
                </p>
                <p className="m-b-none">
                  {instanceImages.length > 0 ? indexInstanceImage + 1 : 0}/
                  {instanceImages.length}
                </p>
              </div>

              <Button
                disabled={
                  loading || instanceImages.length === 0 || isLastInstance
                }
                variant="light"
                onClick={showNextInstance}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ChevronRight}
                  alt="Next Icon"
                />
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
